import React, { useEffect, useState } from "react";
import LoadingState from "../components/basic/loading-state";
import Queries from "../params/queries";
import { formatRelative, isErrorLogLine, accountKeyHidden, mentionsHiddenAccount } from "./shared";
import type {
  AccountRow,
  AuditRow,
  Dashboard,
  FetchApi,
  LogRow,
  WarmupEventRow,
} from "../params/types";

const MODES = [
  { id: "live", label: "Live" },
  { id: "audit", label: "Audit" },
  { id: "warmup", label: "Warm-up" },
  { id: "errors", label: "Errors" },
];

interface LogsProps {
  accounts?: AccountRow[];
  dashboard?: Dashboard | null;
  logAccount?: string | null;
  setLogAccount?: (account: string | null) => void;
  accountLogs?: LogRow[];
  logsPinned?: boolean;
  setLogsPinned?: (pinned: boolean) => void;
  logsRef?: React.RefObject<HTMLDivElement | null>;
  onLogsScroll?: React.UIEventHandler<HTMLDivElement>;
  copyLogs?: () => void | Promise<void>;
  loading?: boolean;
  fetchApi?: FetchApi;
  setError?: (message: string) => void;
  hiddenAccounts?: AccountRow[];
}

function warmupKindLabel(kind: string | null | undefined): string {
  switch (kind) {
    case "watch":
      return "Watched";
    case "like":
      return "Liked";
    case "comments_opened":
      return "Opened comments";
    case "follow":
      return "Followed";
    case "search":
      return "Searched";
    default:
      return kind || "—";
  }
}

export default function Logs({
  accounts = [],
  dashboard,
  logAccount,
  setLogAccount,
  accountLogs = [],
  logsPinned,
  setLogsPinned,
  logsRef,
  onLogsScroll,
  copyLogs,
  loading = false,
  fetchApi,
  setError,
  hiddenAccounts = [],
}: LogsProps) {
  const [mode, setMode] = useState("live");
  const [copied, setCopied] = useState(false);
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [warmupRows, setWarmupRows] = useState<WarmupEventRow[]>([]);
  const [warmupLoading, setWarmupLoading] = useState(true);

  const liveRows = (logAccount ? accountLogs : dashboard?.logs || []).filter(
    (row) =>
      !accountKeyHidden(row.account, hiddenAccounts) &&
      !mentionsHiddenAccount(row.line, hiddenAccounts),
  );
  const errorRows = liveRows.filter(
    (row) => isErrorLogLine(row.line) || row.level === "error",
  );
  const visibleAudit = auditRows.filter(
    (row) =>
      !accountKeyHidden(row.account, hiddenAccounts) &&
      !mentionsHiddenAccount(row.detail, hiddenAccounts) &&
      !mentionsHiddenAccount(row.account, hiddenAccounts),
  );
  const visibleWarmup = warmupRows.filter(
    (row) =>
      !accountKeyHidden(row.account, hiddenAccounts) &&
      !mentionsHiddenAccount(row.detail, hiddenAccounts) &&
      !mentionsHiddenAccount(row.comment_text, hiddenAccounts) &&
      !mentionsHiddenAccount(row.target_username, hiddenAccounts) &&
      !mentionsHiddenAccount(row.keyword, hiddenAccounts),
  );

  useEffect(() => {
    if (mode !== "audit" || !fetchApi) return undefined;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch kickoff
    setAuditLoading(true);
    fetchApi(Queries.Audit, {
      account: logAccount || null,
      limit: 200,
    }).then((res) => {
      if (cancelled) return;
      if (res?.errors) {
        setError?.(res.errors[0]?.message || "Failed to load audit log");
        setAuditRows([]);
      } else {
        setAuditRows(res?.audit || []);
      }
      setAuditLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [mode, logAccount, fetchApi, setError]);

  useEffect(() => {
    if (mode !== "warmup" || !fetchApi) return undefined;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch kickoff
    setWarmupLoading(true);
    fetchApi(Queries.WarmupEvents, {
      account: logAccount || null,
      limit: 200,
    }).then((res) => {
      if (cancelled) return;
      if (res?.errors) {
        setError?.(res.errors[0]?.message || "Failed to load warm-up log");
        setWarmupRows([]);
      } else {
        setWarmupRows(res?.warmup_events || []);
      }
      setWarmupLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [mode, logAccount, fetchApi, setError]);

  const handleCopy = async () => {
    await copyLogs?.();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderAccountTabs = (allLabel: string, ariaLabel: string) => (
    <div className="ib-log-sources" role="tablist" aria-label={ariaLabel}>
      <button
        type="button"
        role="tab"
        aria-selected={!logAccount}
        className={`ib-log-source${!logAccount ? " ib-log-source--active" : ""}`}
        onClick={() => setLogAccount?.(null)}
      >
        {allLabel}
      </button>
      {accounts.map((a) => (
        <button
          key={a.username}
          type="button"
          role="tab"
          aria-selected={logAccount === a.username}
          className={`ib-log-source${
            logAccount === a.username ? " ib-log-source--active" : ""
          }`}
          onClick={() => setLogAccount?.(a.username)}
        >
          @{a.ig_username || a.username}
        </button>
      ))}
    </div>
  );

  const renderLiveConsole = (rows: LogRow[], emptyText: string) => (
    <>
      <div className="ib-card-header">
        <h2>{mode === "errors" ? "Errors" : "Logs"}</h2>
        <div className="ib-logs-tools">
          {!logsPinned && rows.length > 0 && (
            <button
              type="button"
              className="ib-link"
              onClick={() => {
                setLogsPinned?.(true);
                const el = logsRef?.current;
                if (el) el.scrollTop = el.scrollHeight;
              }}
            >
              Jump to latest
            </button>
          )}
          <button
            type="button"
            className="ib-link"
            disabled={!rows.length}
            onClick={handleCopy}
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <span className="ib-muted">{rows.length} lines</span>
        </div>
      </div>
      {renderAccountTabs("System", "Log source")}
      <div className="ib-logs" ref={logsRef} onScroll={onLogsScroll}>
        {loading && !rows.length ? (
          <LoadingState className="ib-empty--dark" label="Loading logs…" />
        ) : (
          !rows.length && (
            <div className="ib-empty ib-empty--dark">{emptyText}</div>
          )
        )}
        {rows.map((row, i) => (
          <div
            key={`${row.at}-${i}`}
            className={`ib-log-line${
              isErrorLogLine(row.line) || row.level === "error"
                ? " ib-log-line--err"
                : row.level === "warn"
                  ? " ib-log-line--warn"
                  : ""
            }`}
          >
            <span className="ib-log-at" title={row.at ?? undefined}>
              {new Date(row.at as string).toLocaleTimeString()}
            </span>
            <span className="ib-log-text">{row.line}</span>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="ib-logs-view">
      <div className="ib-logs-modes" role="tablist" aria-label="Log mode">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            role="tab"
            aria-selected={mode === m.id}
            className={`ib-logs-mode${mode === m.id ? " ib-logs-mode--active" : ""}`}
            onClick={() => setMode(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === "live" && (
        <section className="ib-card ib-logs-card">
          {renderLiveConsole(
            liveRows,
            logAccount
              ? `No logs for @${
                  accounts.find((a) => a.username === logAccount)
                    ?.ig_username || logAccount
                } yet. Run the bot to capture account activity.`
              : "No system log lines yet. Start the bot to see supervisor output.",
          )}
        </section>
      )}

      {mode === "errors" && (
        <section className="ib-card ib-logs-card">
          {renderLiveConsole(
            errorRows,
            "No error lines in the current live buffer.",
          )}
        </section>
      )}

      {mode === "audit" && (
        <section className="ib-card">
          <div className="ib-card-header">
            <h2>Audit</h2>
            <div className="ib-logs-tools">
              {renderAccountTabs("All", "Audit account")}
            </div>
          </div>
          {auditLoading && !visibleAudit.length && (
            <LoadingState label="Loading audit…" />
          )}
          {!auditLoading && !visibleAudit.length && (
            <div className="ib-empty">No audit entries yet.</div>
          )}
          <div className="ib-audit-list">
            {visibleAudit.map((row, i) => (
              <article key={`${row.at}-${i}`} className="ib-audit-card">
                <div className="ib-audit-card__top">
                  <strong>{row.action || "—"}</strong>
                  <span className="ib-muted" title={row.at ?? undefined}>
                    {formatRelative(row.at) || row.at}
                  </span>
                </div>
                <div className="ib-audit-card__meta">
                  <span>{row.actor || "system"}</span>
                  {row.account && <span>@{row.account}</span>}
                </div>
                {row.detail && (
                  <div className="ib-audit-card__detail">{row.detail}</div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {mode === "warmup" && (
        <section className="ib-card">
          <div className="ib-card-header">
            <h2>Warm-up</h2>
            <div className="ib-logs-tools">
              {renderAccountTabs("All", "Warm-up account")}
            </div>
          </div>
          <p className="ib-muted ib-warmup-hint">
            Persistent Reels warm-up actions. The bot opens the comments panel
            sometimes — it does not post comment text today.
          </p>
          {warmupLoading && !visibleWarmup.length && (
            <LoadingState label="Loading warm-up…" />
          )}
          {!warmupLoading && !visibleWarmup.length && (
            <div className="ib-empty">
              No warm-up events yet. Enable warm-up on an account and let it idle.
            </div>
          )}
          <div className="ib-audit-list">
            {visibleWarmup.map((row, i) => (
              <article key={`${row.at}-${row.kind}-${i}`} className="ib-audit-card">
                <div className="ib-audit-card__top">
                  <strong>{warmupKindLabel(row.kind)}</strong>
                  <span className="ib-muted" title={row.at ?? undefined}>
                    {formatRelative(row.at) || row.at}
                  </span>
                </div>
                <div className="ib-audit-card__meta">
                  {row.account && <span>@{row.account}</span>}
                  {row.target_username && (
                    <span>creator @{row.target_username}</span>
                  )}
                  {row.keyword && <span>keyword “{row.keyword}”</span>}
                </div>
                {row.reel_url && (
                  <div className="ib-audit-card__detail">
                    <a
                      href={row.reel_url}
                      target="_blank"
                      rel="noreferrer"
                      className="ib-link"
                    >
                      {row.reel_url}
                    </a>
                  </div>
                )}
                {row.comment_text && (
                  <div className="ib-audit-card__detail">
                    Comment: {row.comment_text}
                  </div>
                )}
                {row.detail && !row.comment_text && (
                  <div className="ib-audit-card__detail">{row.detail}</div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
